import { Component } from '@theme/component';

const WISHLIST_STORAGE_KEY = 'bayse-wishlist';

/**
 * Displays the wishlist icon count in the header.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wishlistCountBracket - The bracket count element.
 *
 * @extends {Component<Refs>}
 */
class WishlistIcon extends Component {
  requiredRefs = ['wishlistCountBracket'];

  /** @type {number} */
  get currentWishlistCount() {
    const text = this.refs.wishlistCountBracket.textContent ?? '';
    const match = text.match(/\((\d+)\)/);
    return match ? parseInt(match[1], 10) : 0;
  }

  /** @param {number} value */
  set currentWishlistCount(value) {
    const countElement = this.refs.wishlistCountBracket;
    if (!countElement) return;

    if (value <= 0) {
      countElement.textContent = '';
      countElement.hidden = true;
      return;
    }

    countElement.textContent = `(${value < 100 ? value : '99+'})`;
    countElement.hidden = false;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('wishlist:update', this.onWishlistUpdate);
    window.addEventListener('storage', this.onStorageChange);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureWishlistCountIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('wishlist:update', this.onWishlistUpdate);
    window.removeEventListener('storage', this.onStorageChange);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * @returns {number}
   */
  getWishlistCount() {
    try {
      const items = JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY) || '[]');
      return Array.isArray(items) ? items.length : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureWishlistCountIsCorrect();
    }
  };

  /**
   * @param {CustomEvent<{ count?: number }>} event
   */
  onWishlistUpdate = (event) => {
    const count = event.detail?.count ?? this.getWishlistCount();
    this.currentWishlistCount = count;

    sessionStorage.setItem(
      'wishlist-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now(),
      })
    );
  };

  /**
   * @param {StorageEvent} event
   */
  onStorageChange = (event) => {
    if (event.key === WISHLIST_STORAGE_KEY) {
      this.onWishlistUpdate(new CustomEvent('wishlist:update'));
    }
  };

  /**
   * Checks if the wishlist count is correct.
   */
  ensureWishlistCountIsCorrect = () => {
    if (!this.refs.wishlistCountBracket) return;

    const storedCount = sessionStorage.getItem('wishlist-count');
    const localCount = this.getWishlistCount();

    if (storedCount === null) {
      this.currentWishlistCount = localCount;
      return;
    }

    const visibleCount = String(this.currentWishlistCount);

    try {
      const { value, timestamp } = JSON.parse(storedCount);

      if (value === visibleCount && localCount === this.currentWishlistCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.currentWishlistCount = count;
          return;
        }
      }
    } catch (_) {
      // no-op
    }

    this.currentWishlistCount = localCount;
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
